import React, { useState } from 'react';
import { formatMeasurement } from './notePresets';

export interface PortalDVIItem {
  point_name: string;
  /**
   * Widened from the three terminal statuses in WP-10 slice 1: a point the
   * technician noted or photographed but has not yet marked is still a finding
   * worth showing, and it arrives 'pending'. Kept as a plain string rather than
   * a four-member union so an unrecognised value from an older or newer server
   * is a rendering question (see `dotFor`) and not a type error at the seam.
   */
  status: string;
  notes: string | null;
  /**
   * WP-07 — the recorded condition value and the shop's own label for it
   * ("Wear: 2mm", "Damage Location: Driver door"). Optional because a portal
   * payload cached or served before WP-07 will not carry them.
   */
  reading_value?: string | null;
  unit?: string | null;
  measurement_label?: string | null;
  photos: Array<{ file_url: string; notes: string | null }>;
}
export interface PortalDVICategory { name: string; items: PortalDVIItem[] }
export interface PortalDVIPayload {
  summary_notes: string | null;
  completed_at: string | null;
  technician_name: string | null;
  counts: { pass: number; attention: number; fail: number };
  categories: PortalDVICategory[];
}

export interface PortalDVISectionProps {
  dvi: PortalDVIPayload;
}

const STATUS_DOT: Record<string, { color: string; label: string }> = {
  pass:      { color: '#22c55e', label: '✓' },
  attention: { color: '#facc15', label: '⚠' },
  fail:      { color: '#ef4444', label: '✗' },
  /**
   * WP-10 slice 1. A point can now reach this component still 'pending' —
   * a technician who photographed a finding and wrote a note before setting the
   * status has recorded something real, and the server projection shares it.
   * Previously the lookup was keyed to exactly three statuses, so ANY other
   * value read back `undefined` and the next line dereferenced it: a single
   * unset status would have thrown away the customer's whole invoice screen.
   * The fallback below makes the lookup total.
   */
  pending:   { color: '#9ca3af', label: '•' },
};
const dotFor = (status: string) => STATUS_DOT[status] ?? STATUS_DOT.pending;

/**
 * Did a human record anything here beyond the status itself?
 *
 * Used only to decide whether a PASSING point is worth a row of its own. A pass
 * with a photo or a measurement ("Wear: 8mm — plenty left") is evidence the
 * customer is paying for; a bare pass is one of thirty ticks and belongs in the
 * summary line.
 */
const hasEvidence = (item: PortalDVIItem) =>
  (!!item.notes && item.notes.trim() !== '') ||
  item.photos.length > 0 ||
  (!!item.reading_value && item.reading_value.trim() !== '');

export const PortalDVISection: React.FC<PortalDVISectionProps> = ({ dvi }) => {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const dateStr = dvi.completed_at ? new Date(dvi.completed_at).toLocaleString() : '';

  return (
    <section style={{
      border: '1px solid #e0e0e0', borderRadius: 8,
      margin: '12px 0', padding: 16, background: 'white',
    }}>
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>Vehicle Inspection</h2>
      <div style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
        Completed by <strong>{dvi.technician_name ?? 'Technician'}</strong> on {dateStr}
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 14 }}>
        <span><span style={{ color: '#22c55e', fontWeight: 700 }}>✓ {dvi.counts.pass}</span> Passed</span>
        <span><span style={{ color: '#facc15', fontWeight: 700 }}>⚠ {dvi.counts.attention}</span> Attention</span>
        <span><span style={{ color: '#ef4444', fontWeight: 700 }}>✗ {dvi.counts.fail}</span> Failed</span>
      </div>
      {dvi.summary_notes && (
        <blockquote style={{
          borderLeft: '3px solid #ccc', paddingLeft: 12,
          margin: '0 0 16px 0', color: '#444', fontStyle: 'italic',
        }}>{dvi.summary_notes}</blockquote>
      )}
      {dvi.categories.map(cat => {
        // Problems first, then passing points that carry evidence of their own.
        // A bare pass — passed, with no note, no photo and no measurement —
        // collapses into the single tail line below rather than taking a row.
        // The server projection DOES send those (`isSharedFinding` shares any
        // point whose status is set), and it should: "we checked the rotors and
        // they are fine" is a result, and the counts line depends on them. What
        // it must not do is spend twenty rows of a phone screen saying it.
        const flagged = cat.items.filter(i => i.status !== 'pass');
        const passedWithEvidence = cat.items.filter(i => i.status === 'pass' && hasEvidence(i));
        const shown = [...flagged, ...passedWithEvidence];
        const passedQuietly = cat.items.filter(i => i.status === 'pass' && !hasEvidence(i));
        if (shown.length === 0 && passedQuietly.length === 0) return null;
        return (
          <div key={cat.name} style={{ marginBottom: 16 }}>
            <h3 style={{
              fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5,
              borderBottom: '1px solid #eee', paddingBottom: 4, marginBottom: 8,
            }}>{cat.name} {flagged.length > 0 && <span style={{ color: '#888', fontWeight: 400 }}>
              ({flagged.length} {flagged.length === 1 ? 'finding' : 'findings'})
            </span>}</h3>
            {shown.map((item, idx) => {
              const dot = dotFor(item.status);
              return (
                <div key={idx} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ color: dot.color, fontSize: 16, fontWeight: 700 }}>
                      {dot.label}
                    </span>
                    <strong>{item.point_name}</strong>
                    {/*
                      WP-07: same rendering rule as the public estimate view and
                      the invoice — via formatMeasurement so the unit is not
                      doubled when the preset value already carries it.
                    */}
                    {item.reading_value && item.reading_value.trim() !== '' && (
                      <strong data-testid={`portal-measurement-${idx}`}>
                        {formatMeasurement(item.reading_value, item.unit, item.measurement_label)}
                      </strong>
                    )}
                  </div>
                  {item.notes && (
                    <div style={{ marginLeft: 24, color: '#555', fontSize: 13 }}>{item.notes}</div>
                  )}
                  {item.photos.length > 0 && (
                    <div style={{
                      marginLeft: 24, marginTop: 4,
                      display: 'flex', gap: 6, flexWrap: 'wrap',
                    }}>
                      {item.photos.map((p, pi) => (
                        <img
                          key={pi} src={p.file_url} alt={item.point_name}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                          onClick={() => setLightbox(p.file_url)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {passedQuietly.length > 0 && (
              <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                ✓ {passedQuietly.length} other point{passedQuietly.length === 1 ? '' : 's'} passed
              </div>
            )}
          </div>
        );
      })}
      {lightbox && (
        <div
          role="dialog"
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, cursor: 'pointer',
          }}
        >
          <img src={lightbox} alt="" style={{ maxWidth: '90%', maxHeight: '90%' }} />
        </div>
      )}
    </section>
  );
};

export default PortalDVISection;
